import React, { useEffect, useRef, useState } from 'react';

import { tooltipMessages } from '../../../translates/tooltipMessages';
import { useIntlReexport } from '../../hooks/useIntlReexport';
import { useAudioElementContext } from './AudioElementContext';
import { AutoplayDisallowedOverlay } from './AutoplayDisallowedOverlay';
import { AudioCountdown } from './Countdown/AudioCountdown';
import {
  AudioElement,
  AudioElementHandler,
  AudioSpeed,
  AudioSpeedCollapsibleToolbar,
  AudioToolbar,
  PlayButton,
  PlayStatus,
} from '@ael/shared-components-library';

const REWIND_BACK_SEC = 10;

interface AudioProps {
  src: string;
  isCollapsible: boolean;

  captionSrc?: string;
  captionsEnabled: boolean;
  onCaptionTextChange: (text: string | null) => void;
  onCaptionsClick?: () => void;

  // Events
  onLoadedMetadata?: (duration: number) => void;
  onPlaying?: () => void;
  onPause?: () => void;
  onTimeUpdate?: (currentTime: number) => void;
  onEnded?: () => void;
}

export const Audio = (props: AudioProps) => {
  const { isMuted, toggleMute, isAutoPlayOn, audioSpeed, changeSpeed } = useAudioElementContext();
  const [playStatus, setPlayStatus] = useState(PlayStatus.NotStartedOrPaused);
  const [currentAudioTime, setCurrentAudioTime] = useState(0);
  const [duration, setDuration] = useState<number | null>(null);

  const [showAutoplayDisallowed, setShowAutoplayDisallowed] = useState(false);
  const audioElementHandlerRef = useRef<AudioElementHandler>(null);

  const { formatMessage } = useIntlReexport();

  useEffect(() => {
    const audioElementHandler = audioElementHandlerRef.current;
    if (!audioElementHandler) {
      return;
    }

    if (!isAutoPlayOn) {
      return;
    }
    audioElementHandler.playFromStart(() => setShowAutoplayDisallowed(true));
    return () => audioElementHandler.pause();
  }, [isAutoPlayOn, audioElementHandlerRef, setShowAutoplayDisallowed]);

  const onChangeSpeed = (speed: AudioSpeed) => {
    changeSpeed(speed);
    const audioElementHandler = audioElementHandlerRef.current;
    if (!audioElementHandler) {
      return;
    }

    audioElementHandler.changePlaybackRate(speed);
  };

  const onLoaded = (loadedDuration: number) => {
    setDuration(loadedDuration);
    props.onLoadedMetadata?.(loadedDuration);
  };
  const onEnded = () => {
    setPlayStatus(PlayStatus.Finished);
    props.onEnded?.();
  };
  const onTimeUpdate = (time: number) => {
    setCurrentAudioTime(time);
    props.onTimeUpdate?.(time);
  };
  const onPaused = () => {
    setPlayStatus(PlayStatus.NotStartedOrPaused);
    props.onPause?.();
  };
  const onPlaying = () => {
    setPlayStatus(PlayStatus.Playing);
    props.onPlaying?.();
  };

  return (
    <>
      {showAutoplayDisallowed && (
        <AutoplayDisallowedOverlay
          onStartPlayingClick={() => {
            void audioElementHandlerRef.current?.playFromStart(() => setShowAutoplayDisallowed(true));
            setShowAutoplayDisallowed(false);
          }}
          onCancel={() => setShowAutoplayDisallowed(false)}
        />
      )}
      <AudioElement
        ref={audioElementHandlerRef}
        audioSrc={props.src}
        captionsSrc={props.captionSrc}
        isMuted={isMuted}
        onLoadedMetadata={onLoaded}
        onPlaying={onPlaying}
        onTimeUpdate={onTimeUpdate}
        onEnded={onEnded}
        onPause={onPaused}
        onCaptionsChanged={props.onCaptionTextChange}
        playbackRate={audioSpeed}
      />
      <PlayButton
        playStatus={playStatus}
        onClick={() => audioElementHandlerRef.current?.toggle()}
        big={!props.isCollapsible}
        label={
          <AudioCountdown
            isPlaying={playStatus === PlayStatus.Playing}
            duration={duration}
            currentTime={currentAudioTime}
          />
        }
        tooltipTextPlay={formatMessage(tooltipMessages.play_audio)}
        tooltipTextPause={formatMessage(tooltipMessages.pause_audio)}
      />
      <AudioToolbar
        captionSrc={props.captionSrc}
        rewindTime={REWIND_BACK_SEC}
        captionsEnabled={props.captionsEnabled}
        isMuted={isMuted}
        isCollapsible={props.isCollapsible}
        audioSpeed={audioSpeed}
        onRewindBack={() => audioElementHandlerRef.current?.rewindBack(REWIND_BACK_SEC)}
        onCaptionsClick={props.onCaptionsClick}
        onToggleMute={toggleMute}
        onChangeSpeed={onChangeSpeed}
        tooltipMessages={{
          mute: formatMessage(tooltipMessages.mute),
          unmute: formatMessage(tooltipMessages.unmute),
          rewind: formatMessage(tooltipMessages.rewind),
          subtitles: formatMessage(tooltipMessages.subtitles),
        }}
      />
      {!props.isCollapsible && <AudioSpeedCollapsibleToolbar currentSpeed={audioSpeed} onSpeedChange={onChangeSpeed} />}
    </>
  );
};
